/**
 * Derives timeline nodes and inspector categories from structured trace events.
 */

/** @typedef {{ id: number, timestamp?: string, level?: string, message?: string, taskId?: string, agentId?: string, eventType?: string|null, component?: string|null, data?: Record<string, unknown>, source?: string, isBootstrap?: boolean }} TraceLogEntry */

export const INSPECTOR_SECTIONS = {
  memory: { label: '🧠 Memory / Context', keys: ['MEMORY_FETCHED', 'MEMORY_RECALLED', 'MEMORY_CONSOLIDATED', 'ACTIVITY_SAVED'] },
  context: { label: '📄 Context', keys: ['CONTEXT_BUILT'] },
  llm: { label: '⚡ LLM Execution', keys: ['LLM_INFERENCE_COMPLETE'] },
  tools: { label: '🔧 Tools', keys: ['TOOL_EXECUTION_COMPLETE', 'VECTOR_INGESTION_COMPLETE', 'CONSULTATION_COMPLETE'] },
  final: { label: '📤 Final decision', keys: ['AGENT_TURN_COMPLETE'] },
  human: { label: '👤 Human', keys: ['HUMAN_APPROVAL'] },
  asyncDoc: { label: '📎 Async / docs', keys: ['ASYNC_EXTRACTION_STARTED', 'ASYNC_EXTRACTION_SUCCESS'] },
  routing: { label: '🔀 Routing', keys: ['AGENT_RESOLVED', 'AGENT_HYDRATED', 'INTERNAL_ROUTING', 'MESSAGE_DISPATCHED'] },
  other: { label: '📋 Other', keys: [] }
};

/**
 * @param {string|undefined} eventType
 * @returns {keyof typeof INSPECTOR_SECTIONS}
 */
export function sectionForEventType(eventType) {
  if (!eventType) return 'other';
  for (const [section, { keys }] of Object.entries(INSPECTOR_SECTIONS)) {
    if (section === 'other') continue;
    if (keys.includes(eventType)) return section;
  }
  return 'other';
}

/**
 * @param {TraceLogEntry[]} entries
 * @returns {TraceLogEntry[]}
 */
export function traceEventsOnly(entries) {
  return (entries || []).filter((e) => !e.isBootstrap);
}

/**
 * Group key for timeline: component + agent identity. ``component`` is always
 * present by the time we reach this function — untagged rows are filtered out
 * upstream in ``buildTimelineNodes`` so the old ``'UNKNOWN'`` fallback is
 * gone. ``agentId`` may still be empty for non-agent emitters (e.g. the
 * router) and falls back to ``System``.
 * @param {TraceLogEntry} e
 */
function groupKey(e) {
  const comp = String(e.component).trim();
  const agent = (e.agentId || 'System').toString();
  return `${comp}::${agent}`;
}

/**
 * A log row is a *tagged trace event* iff both ``event_type`` and ``component``
 * are set to non-empty, non-placeholder values. Historical S3 files (runs
 * predating this cut) may still contain ambient rows where ``component`` is
 * null or literally ``"UNKNOWN"``; we drop those here so clicking "Merge S3"
 * on an old task can't resurface a junk bucket in the timeline.
 * @param {TraceLogEntry} e
 */
function isTaggedTraceEvent(e) {
  const comp = e?.component == null ? '' : String(e.component).trim();
  const et = e?.eventType == null ? '' : String(e.eventType).trim();
  if (!comp || !et) return false;
  if (comp.toUpperCase() === 'UNKNOWN') return false;
  return true;
}

/**
 * @param {TraceLogEntry[]} entries — raw runner logs (may include bootstrap)
 * @returns {Array<{ id: string, title: string, subtitle: string, status: string, events: TraceLogEntry[], firstTs: string, lastTs: string }>}
 */
export function buildTimelineNodes(entries) {
  const events = traceEventsOnly(entries)
    .filter(isTaggedTraceEvent)
    .slice()
    .sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));

  if (events.length === 0) return [];

  const nodes = [];
  let current = null;

  for (const ev of events) {
    const key = groupKey(ev);
    if (!current || current.key !== key) {
      if (current) nodes.push(current);
      current = {
        key,
        events: [ev],
        component: String(ev.component).trim(),
        agentId: ev.agentId || 'System'
      };
    } else {
      current.events.push(ev);
    }
  }
  if (current) nodes.push(current);

  return nodes.map((n, i) => ({
    id: `node-${i}-${n.key}`,
    title: formatNodeTitle(n.component, n.agentId),
    subtitle: n.events[n.events.length - 1]?.eventType || n.events[0]?.eventType || '',
    status: deriveNodeStatus(n.events),
    events: n.events,
    firstTs: n.events[0]?.timestamp || '',
    lastTs: n.events[n.events.length - 1]?.timestamp || '',
    key: n.key
  }));
}

function formatNodeTitle(component, agentId) {
  const c = (component || 'Step').toString();
  const a = (agentId || '').toString();
  if (a && a !== 'System') return `${c} · ${a}`;
  return c;
}

/**
 * @param {TraceLogEntry[]} events
 */
function deriveNodeStatus(events) {
  for (const e of events) {
    if (e.eventType === 'HUMAN_APPROVAL') return 'pending';
  }
  let hasError = false;
  let hasWarn = false;
  let complete = false;
  for (const e of events) {
    const L = (e.level || 'INFO').toUpperCase();
    if (L === 'ERROR' || L === 'CRITICAL') hasError = true;
    if (L === 'WARNING' || L === 'WARN') hasWarn = true;
    if (e.eventType === 'AGENT_TURN_COMPLETE' || e.eventType === 'TOOL_EXECUTION_COMPLETE') complete = true;
  }
  if (hasError) return 'error';
  if (hasWarn) return 'warn';
  if (complete) return 'success';
  return 'running';
}

export const NODE_STATUS_STYLES = {
  success: { ring: 'ring-emerald-500/40', bg: 'bg-emerald-50', dot: 'bg-emerald-500', label: 'Done' },
  running: { ring: 'ring-blue-500/30', bg: 'bg-blue-50', dot: 'bg-blue-500 animate-pulse', label: 'Active' },
  warn: { ring: 'ring-amber-500/40', bg: 'bg-amber-50', dot: 'bg-amber-500', label: 'Warning' },
  error: { ring: 'ring-red-500/40', bg: 'bg-red-50', dot: 'bg-red-500', label: 'Error' },
  pending: { ring: 'ring-violet-500/40', bg: 'bg-violet-50', dot: 'bg-violet-500', label: 'Waiting' },
  tool: { ring: 'ring-cyan-500/40', bg: 'bg-cyan-50', dot: 'bg-cyan-600', label: 'Tool' },
  idle: { ring: 'ring-gray-200', bg: 'bg-gray-50', dot: 'bg-gray-400', label: '' }
};

/**
 * Refine status for display (tool-heavy nodes).
 */
export function nodeStatusForDisplay(node) {
  if (node.status === 'success' && node.events?.some((e) => e.eventType?.includes('TOOL'))) {
    return { ...NODE_STATUS_STYLES.tool, key: 'tool' };
  }
  return NODE_STATUS_STYLES[node.status] || NODE_STATUS_STYLES.idle;
}

/**
 * Group events by inspector section for the selected node.
 * @param {TraceLogEntry[]} events
 */
export function groupEventsBySection(events) {
  /** @type {Record<string, TraceLogEntry[]>} */
  const out = {};
  for (const key of Object.keys(INSPECTOR_SECTIONS)) {
    out[key] = [];
  }
  for (const e of events) {
    const sec = sectionForEventType(e.eventType || undefined);
    out[sec].push(e);
  }
  return out;
}

/**
 * Dedupe S3 rows against existing stream (rough).
 * @param {TraceLogEntry[]} existing
 * @param {TraceLogEntry[]} incoming
 */
export function dedupeTraceRows(existing, incoming) {
  const seen = new Set(
    existing.map((r) => `${r.timestamp}|${r.eventType}|${(r.message || '').slice(0, 120)}`)
  );
  return incoming.filter((r) => {
    const k = `${r.timestamp}|${r.eventType}|${(r.message || '').slice(0, 120)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
